// @flow
import type { ChildrenArray, Element } from 'react';
import React from 'react';
import Fraction from 'fraction.js';
import JXG from 'jsxgraph';
import 'jsxgraph/distrib/jsxgraph.css';
import LatexInternal from './LatexInternal.js';
import color from './color.js';
import {
  useValidationErrorsFunctions,
  useCustomValidation,
} from './validation/runtime-checks/index.js';
import { types as t } from './validation/runtime-checks/index.js';
import type { CompilerInjectedProps } from './validation/runtime-checks/index.js';
import Figure from './Figure.js';
import { figureSizes, illustrationColor } from './designSystem.js';
import withValidation from './withValidation.js';
// This is 10kb 🤔, probably should just write a bespoke version
// for doing deep value/object whose values can only be primitives
// (or more arrays/objects).
import isEqual from 'lodash.isequal';

const { abs, floor } = Math;

const BoardContext = React.createContext(null);

const useUniqueId = () => {
  const [id] = React.useState(() => String(Math.random()));
  return id;
};

const PLANE_BG = '#fff';

const plotColors = {
  // Yes this is confusing, we now map mountainMeadow to niagra. mountainMeadow
  // didn't meet accessibility standards and so we have to use niagra instead.
  // We have 2.2k references in JSX documents of "mountainMeadow" though, which
  // will be a lot of effort to change and republish, so we're taking a gross
  // and confusing shortcut here.
  // TODO: add niagra as an option, codemod all docs to use niagra, remove mountainMeadow
  // from the *validation* spec, but keep it in the runtime lookup. Then ensure all codemoded
  // docs are republished to mathspace, then remove mountainMeadow from the runtime lookup.
  mountainMeadow: illustrationColor.niagra,
  lochmara: illustrationColor.lochmara,
  purpleHeart: illustrationColor.purpleHeart,
  pickledBluewood: illustrationColor.pickledBluewood,
};

const strokeStyles = {
  strokeColor: plotColors.mountainMeadow,
  strokeWidth: 2,
};

export type Props = {|
  children?: ChildrenArray<Element<GraphComponent>>,
  width: number,
  height: number,
  title?: string,
  xAxis?: AxisConfig,
  yAxis?: AxisConfig,
  // There are certain cases where we don't want <Figure> to
  // apply its default margins. At time of writing, SolidifyFigureGrid
  // and SolidifyAnnotatedFigure both require this. I'm not really happy
  // with this approach though, so need to rethink it sometime.
  _removeFigureMargin?: boolean,
  ...CompilerInjectedProps,
|};

type GraphComponent =
  | typeof Point
  | typeof Line
  | typeof Ray
  | typeof LineSegment
  | typeof Vector
  | typeof FunctionPlot
  | typeof Polygon
  | typeof Circle
  | typeof Ellipse
  | typeof Hyperbola
  | typeof Parabola;

type AxisConfig = {|
  min?: number,
  max?: number,
  interval?: number,
  intervalSubdivisions?: number,
  unit?: string, // latex
  scale?: number,
  numberFormat?: 'decimal' | 'improperFraction' | 'mixedFraction',
  decimalPlaces?: number,
  label?: string, // latex
|};

const axisSpec = t.exact({
  min: t.optional(t.number),
  max: t.number,
  interval: t.optional(t.number),
  intervalSubdivisions: t.optional(t.number),
  unit: t.optional(t.latex),
  scale: t.optional(t.number),
  numberFormat: t.optional(
    t.unionOfValues('decimal', 'improperFraction', 'mixedFraction'),
  ),
  // Ideally would only be allowed when 'decimal' numberFormat is used
  decimalPlaces: t.optional(t.number),
  label: t.optional(t.latex),
});

export const spec = {
  // width and height are now required, but we have maintained the old
  // default logic so current content does not break.
  // We will unwind it when existing content has been updated to
  // reflect the new validation.
  width: t.number,
  height: t.number,
  title: t.optional(t.string),
  xAxis: t.optional(axisSpec),
  yAxis: t.optional(axisSpec),
  children: t.optional(
    t.childrenArray(
      t.unionOfTypes(
        t.elementType(Point),
        t.elementType(Line),
        t.elementType(Ray),
        t.elementType(LineSegment),
        t.elementType(Vector),
        t.elementType(FunctionPlot),
        t.elementType(FunctionInequality),
        t.elementType(Polygon),
        t.elementType(Circle),
        t.elementType(Ellipse),
        t.elementType(Hyperbola),
        t.elementType(Parabola),
      ),
    ),
  ),
};

function validateStrictPositive(constraint: mixed): boolean {
  return constraint != null
    ? typeof constraint === 'number' && constraint > 0
    : true;
}

const DEFAULT_WIDTH = 260;
const DEFAULT_HEIGHT = 260;

function useHasEnteredViewport() {
  const supportsIntersectionObserver =
    typeof window !== 'undefined' && 'IntersectionObserver' in window;
  const ref = React.useRef(null);
  // For browsers that dont support intersection observer we will just say
  // that the thing is the in viewport. This means lazy loading is disabled
  // unless you support intersectionobserver.
  const [hasEnteredViewport, setHasEnteredViewport] = React.useState(
    supportsIntersectionObserver ? false : true,
  );
  React.useEffect(() => {
    if (!supportsIntersectionObserver) return;
    if (ref.current === null) return;

    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) setHasEnteredViewport(true);
    });
    observer.observe(ref.current);
  }, [ref]);
  return [hasEnteredViewport, ref];
}

function CartesianPlane(props: Props) {
  const { addErrors } = useValidationErrorsFunctions();
  const invalidConstraints = [
    {
      constraint: 'xAxis interval',
      invalid: !validateStrictPositive(props.xAxis?.interval),
    },
    {
      constraint: 'yAxis interval',
      invalid: !validateStrictPositive(props.yAxis?.interval),
    },
    {
      constraint: 'xAxis intervalSubdivisions',
      invalid: !validateStrictPositive(props.xAxis?.intervalSubdivisions),
    },
    {
      constraint: 'yAxis intervalSubdivisions',
      invalid: !validateStrictPositive(props.yAxis?.intervalSubdivisions),
    },
    {
      constraint: 'xAxis scale',
      invalid: !validateStrictPositive(props.xAxis?.scale),
    },
    {
      constraint: 'yAxis scale',
      invalid: !validateStrictPositive(props.yAxis?.scale),
    },
  ];

  // Ad-hoc assertion: ensure that the dimensions are one of the allowed figure sizes from our design system.
  React.useEffect(() => {
    if (!props._performValidation) return;
    const { width = DEFAULT_WIDTH, height = DEFAULT_HEIGHT } = props;

    invalidConstraints.forEach((validation) => {
      if (validation.invalid) {
        addErrors([
          {
            message: `${validation.constraint} can't be less than or equal to 0`,
            ...(/^xAxis/.test(validation.constraint)
              ? props._sourceLocations.xAxis
              : props._sourceLocations.yAxis),
          },
        ]);
      }
    });

    if (
      figureSizes.find((s) => s.width === width && s.height === height) !==
      undefined
    )
      return;

    const allowedSizesMsg = figureSizes
      .map((s) => `- ${s.width}x${s.height}`)
      .join('\n');
    addErrors([
      {
        message: [
          `CartesianPlane cannot be ${width}x${height}.`,
          `You must use one of the following dimensions:`,
          allowedSizesMsg,
        ].join('\n'),
        ...props._sourceLocations.CartesianPlane,
      },
    ]);
  });

  if (invalidConstraints.filter((v) => v.invalid).length > 0) {
    return null;
  }

  return <InternalCartesianPlaneUI {...props} />;
}
CartesianPlane.spec = spec;
CartesianPlane = withValidation(CartesianPlane);

function InternalCartesianPlaneUI(props: Props) {
  let {
    width = DEFAULT_WIDTH,
    height = DEFAULT_HEIGHT,
    children,
    title = '',
    xAxis = {},
    yAxis = {},
  } = props;
  const axisDefaults = {
    min: -5,
    max: 5,
    interval: 5,
    intervalSubdivisions: 5,
    unit: '',
    scale: 1,
    numberFormat: 'decimal',
    decimalPlaces: 1,
    label: '',
  };
  xAxis = { ...axisDefaults, label: 'x', ...xAxis };
  yAxis = { ...axisDefaults, label: 'y', ...yAxis };

  const id = useUniqueId();
  const [board, setBoard] = React.useState(null);
  const xMin = xAxis.min;
  const xMax = xAxis.max;
  const yMin = yAxis.min;
  const yMax = yAxis.max;

  // We use this id triple to ensure that if the width or height changes
  // that we delete the old DOM node, create a new DOM node in its place
  // and then initialize a fresh board on that new DOM node. The reason
  // we do this is because, AFAICT, you cannot alter the dimensions of a board
  // after initialization (it reads the dimensions from the DOM node at
  // initialization time)
  // See https://github.com/jsxgraph/jsxgraph/blob/b72c5b981c5d90a09f8c52daf92cbbfcc87624d4/src/jsxgraph.js#L206
  const boardId = `${id}:${width}:${height}`;
  const nodeRef = React.useRef(null);
  React.useEffect(() => {
    // because the id component of the boardId is generated with the use of Math.random,
    // hydrating this component in the frontend from an SSR render causes the ids to not
    // correspond correctly. In order to get the actual value of the id, we use a ref to that node instead
    const board = JXG.JSXGraph.initBoard(nodeRef.current.id, {
      // TODO explain why this looks a bit quirky.
      boundingbox: [xMin, yMax, xMax, yMin],
      showCopyright: false,
      showNavigation: false,
      zoom: false,
      pan: false,
    });
    setBoard(board);
    return () => {
      // Effect cleanup functions are run in an in-order sequence, rather than the
      // post-order sequence of effects themselves. We need to cleanup the board
      // after all the children have cleaned up. By scheduling a microtask we can
      // ensure that we free the board after all the other cleanup effects
      // have run, but before any new tasks could have been scheduled.
      scheduleMicroTask(() => {
        JXG.JSXGraph.freeBoard(board);
      });
    };

    // We still want boardId here even if it isn't used in the above code
    // because we want it to rerun whenever height/width change and a fresh
    // node is added to the dom
  }, [boardId]);

  // If the definitions of the axes are changed, ensure that the
  // we update the bounding box of the underlying board.
  React.useEffect(() => {
    if (board === null) return;
    board.setBoundingBox([xMin, yMax, xMax, yMin]);
  }, [xMin, yMax, xMax, yMin]);

  // Functions for mapping from logical -> pixel domains

  // Somewhere in JSXGraph's implementation details their precision
  // is a bit off relative to ours, which is causing them to draw
  // their lines at the subpixel resolution slightly differently to ours.
  // This adjustment (empirically) appears to give us a "close-enough"
  // correspondence. Their imprecision oscillates though, relative to our values.
  const adjustment = 0.5;

  // TODO Probably turn these 3 lines into a scale() function similar to d3
  const xDomain = abs(xMax - xMin);
  const xRatio = width / xDomain;
  const toPxX = (x) => (x - xMin) * xRatio - adjustment;

  const domainY = abs(yMax - yMin);
  const ratioY = height / domainY;
  const toPxY = (y) => -(y - yMax) * ratioY;

  const [hasEnteredViewport, rootRef] = useHasEnteredViewport();

  // The jsxgraph library blocks the UI thread when rendering plots. To prevent the UI
  // thread locking up when loading a document with CartesianPlane's in it we delay
  // the plotting until the CartesianPlane enters the viewport. This laziness complicates
  // things for our PDF generation service (since we need all the CartesianPlanes to render
  // before we can generate a correct PDF), so we allow the runtime environment to
  // opt in to eager rendering via a global property on window.
  //
  // NOTE the board will also not be available on the first render (as board
  // initialization requires a DOM node to be present so has to occur in an effect)
  // so we don't render the plots until the board has been created.
  const shouldRenderPlots =
    board !== null &&
    (hasEnteredViewport || Boolean(window.shouldEagerlyRenderCartesianPlanes));

  return (
    <BoardContext.Provider value={board}>
      <Figure ref={rootRef} _removeFigureMargin={props._removeFigureMargin}>
        <div
          style={{
            position: 'relative',
            textAlign: 'center',
            isolation: 'isolate',
          }}
        >
          {title && (
            <>
              <div
                style={{
                  fontSize: 16,
                  fontWeight: 'bold',
                }}
              >
                {title}
              </div>
              <div style={{ height: 20 }} />
            </>
          )}

          <div
            style={{
              display: 'block',
              position: 'relative',
              width,
              height,
              margin: '0 auto',
              background: PLANE_BG,
            }}
          >
            <InternalXAxis
              width={width}
              {...xAxis}
              yMin={yMin}
              yMax={yMax}
              toPxX={toPxX}
              toPxY={toPxY}
            />
            <InternalYAxis
              height={height}
              {...yAxis}
              xMin={xMin}
              xMax={xMax}
              toPxX={toPxX}
              toPxY={toPxY}
            />
            <InternalGrid
              width={width}
              height={height}
              xMin={xAxis.min}
              xMax={xAxis.max}
              yMin={yAxis.min}
              yMax={yAxis.max}
              toPxX={toPxX}
              toPxY={toPxY}
              // These need to concord with the InternalXAxis/InternalYAxis
              xInterval={xAxis.interval}
              yInterval={yAxis.interval}
              xScale={xAxis.scale}
              yScale={yAxis.scale}
              xAxis={xAxis}
              yAxis={yAxis}
            />

            <div
              ref={nodeRef}
              // This key is very important to ensure that we get a fresh backing DOM node
              // when the boardId changes (which will change when width/height changes).
              key={boardId}
              id={boardId}
              style={{
                borderRadius: 4,
                width,
                height,
                // border: '1px solid #34495E',
                userSelect: 'none',
                position: 'relative',
                zIndex: 1,
              }}
            />
            {/* Place a cover atop the graph to prevent any possible user interactions */}
            <div
              key="cover"
              style={{
                width: '100%',
                height: '100%',
                position: 'absolute',
                top: 0,
                left: 0,
                zIndex: 2,
              }}
            />
            {shouldRenderPlots && children}
          </div>
        </div>
      </Figure>
    </BoardContext.Provider>
  );
}

// TODO Having the intervals emanate from the origin make sense
// but I'm not sure that it makes sense to base
// I think a human typically wants the bottom left of the graph
// to be where the scale begins when they are note able to see
// the actual axes.
function logicalTickValues(min, max, interval, scale = 1) {
  const scaledInterval = scale * interval;

  if (min > 0 || max < 0) {
    const numTicks = floor(abs(max - min) / scaledInterval) + 1; // +1 so that its inclusive
    const ticks = [];
    for (let i = 0; i < numTicks; i++) {
      if (min > 0) ticks.push(min + i * scaledInterval);
      else ticks.push(max - i * scaledInterval);
    }
    return ticks;
  } else {
    const numNegativeTicks = floor(abs(min / scaledInterval));
    const numPositiveTicks = floor(max / scaledInterval);
    const ticks = [];
    for (let i = 0; i < numNegativeTicks; i++) {
      ticks.push(-((i + 1) * scaledInterval));
    }
    ticks.push(0);
    for (let i = 0; i < numPositiveTicks; i++) {
      ticks.push((i + 1) * scaledInterval);
    }
    ticks.sort((a, b) => a - b);
    return ticks;
  }
}

// TODO can we unify the Axis components in some way?
const InternalXAxis = ({
  width,
  min,
  max,
  interval = 1,
  scale = 1,
  yMin,
  yMax,
  toPxX,
  toPxY,
  unit = '', // Latex
  label = '',
  numberFormat,
  decimalPlaces,
}) => {
  // Decide where to vertically place the InternalXAxis in the plane.
  let yPos;
  if (yMax > 0 && yMin > 0) {
    yPos = yMin;
  } else if (yMax < 0 && yMin < 0) {
    yPos = yMax;
  } else {
    yPos = 0;
  }

  // The "full" requirement is important as cases where one of the axis
  // is not rooted at 0 is confusing without a bunch of other specialized
  // labelling logic, since the point where the axis cross have a different
  // meaning on each axis.
  const isFullQ1 = min === 0 && max >= 0 && yMin === 0 && yMax >= 0;

  return (
    <div
      style={{
        position: 'absolute',
        zIndex: 1,
        width,
        height: 50,
        top: 0,
        transform: `translateY(${toPxY(yPos)}px)`,
      }}
    >
      {/* Major ticks */}
      {logicalTickValues(min, max, interval, scale).map((value, i, values) => {
        // We do specialized checks for quadrant 1 as we do want the 0
        // label for the origin when both axis are rooted at 0.
        if (isFullQ1) {
          // Don't render the upper bound label in Q1
          if (i === values.length - 1) return null;
        } else {
          // Don't render the origin label
          if (value === 0) return null;
          // Don't render the end bound labels of the range.
          if (i === 0 || i === values.length - 1) return null;
        }

        return (
          <div
            key={i}
            style={{
              position: 'absolute',
              left: 0,
              top: 0,
              transform: `translateX(${toPxX(value)}px)`,
            }}
          >
            <div style={{ transform: 'translate(-50%, 2px)' }}>
              <InternalLabelText
                representation={numberFormat}
                decimalPlaces={decimalPlaces}
                value={value}
                unit={unit}
                scale={scale}
              />
            </div>
          </div>
        );
      })}

      {/* Axis line */}
      <div
        style={{
          position: 'absolute',
          transform: 'translateY(calc(-50% - 0.5px))',
          background: '',
        }}
      >
        <AxisLine lineCapStart={min < 0} lineCapEnd={max > 0} length={width} />
      </div>

      {/* Axis label */}
      {label && (
        <div
          style={{
            position: 'absolute',
            top: -4,
            right: 5,
            transform: `translateY(-100%)`,
            lineHeight: 1,
          }}
        >
          <LatexInternal expression={label} />
        </div>
      )}
    </div>
  );
};

const AxisLine = ({ length, lineCapStart = false, lineCapEnd = false }) => {
  const depth = 7;

  const startLineCap = lineCapStart
    ? ['M 7 3', 'v -3', 'l -7 3.5', 'l 7 3.5', 'v -3']
    : ['M 7 3', 'h -7', 'v 1', 'h 7'];

  const endLineCap = lineCapEnd
    ? ['v 3', 'l 7, -3.5', 'l -7 -3.5', 'v 3']
    : ['h 7', 'v -1', 'h -7'];

  const path = [
    ...startLineCap,
    `h ${length - depth * 2}`,
    ...endLineCap,
    `h -${length - depth * 2}`,
    'Z',
  ];

  return (
    <svg
      width={length}
      height={depth}
      viewBox={`0 0 ${length} ${depth}`}
      style={{ display: 'block' }}
    >
      <path d={path.join(' ')} fill={color.ebony} />
    </svg>
  );
};

// TODO can we unify the Axis components in some way?
const InternalYAxis = ({
  height,
  min,
  max,
  interval = 1,
  scale = 1,
  xMin,
  xMax,
  toPxX,
  toPxY,
  unit = '',
  label = '',
  numberFormat,
  decimalPlaces,
}) => {
  // Determine the horizontal positioning for the axis
  let xPos;
  if (xMax > 0 && xMin > 0) {
    xPos = xMin;
  } else if (xMax < 0 && xMin < 0) {
    xPos = xMax;
  } else {
    xPos = 0;
  }

  return (
    <div
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        transform: `translateX(${toPxX(xPos)}px)`,
        zIndex: 1,
      }}
    >
      {/* Major ticks */}
      {logicalTickValues(min, max, interval, scale).map((value, i, values) => {
        // Don't render a label for the origin.
        if (value === 0) return null;

        // Don't render ticks for the end bounds of the range.
        if (i === 0 || i === values.length - 1) return null;

        return (
          <div
            key={i}
            style={{
              position: 'absolute',
              right: 0,
              top: toPxY(value),
            }}
          >
            <span
              style={{
                transform: 'translateY(-50%)',
                position: 'absolute',
                right: 4,
              }}
            >
              <InternalLabelText
                representation={numberFormat}
                decimalPlaces={decimalPlaces}
                value={value}
                unit={unit}
                scale={scale}
              />
            </span>
          </div>
        );
      })}

      {/* Axis */}
      <div
        style={{
          position: 'absolute',
          transform: 'rotate(90deg) translateY(calc(-50% - 0.5px))',
          transformOrigin: 'top left',
          background: '',
        }}
      >
        <AxisLine lineCapStart={max > 0} lineCapEnd={min < 0} length={height} />
      </div>

      {/* Axis label */}
      {label && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 8,
            lineHeight: 1,
          }}
        >
          <LatexInternal expression={label} />
        </div>
      )}
    </div>
  );
};

const InternalLabelText = ({
  representation,
  decimalPlaces,
  value,
  unit = '',
  scale = 1,
}) => {
  const scaledValue = value / scale;
  let latex = '';

  // TODO do exhaustive "pattern" matching here
  if (representation === 'improperFraction') {
    latex = new Fraction(scaledValue).toLatex();
  } else if (representation === 'mixedFraction') {
    latex = new Fraction(scaledValue).toLatex(true);
  } else if (representation === 'decimal') {
    latex = Number.isInteger(scaledValue)
      ? String(scaledValue)
      : String(scaledValue.toFixed(decimalPlaces));
  }

  latex += unit;

  return (
    <LatexInternal
      expression={latex}
      fontSize={12}
      style={{
        // we want precise alignment unaffected by inline algos
        display: 'block',
        // This puts a 1px outline around the glyphs in order to increase readability
        textShadow: `
          -1px -1px 0 #fff,
          0 -1px 0 #fff,
          1px -1px 0 #fff,
          1px 0 0 #fff,
          1px 1px 0 #fff,
          0 1px 0 #fff,
          -1px 1px 0 #fff,
          -1px 0 0 #fff
        `,
      }}
    />
  );
};

const InternalGrid = ({
  width,
  height,
  xMin,
  xMax,
  yMin,
  yMax,
  toPxX,
  toPxY,
  xInterval,
  yInterval,
  xScale = 1,
  yScale = 1,
  xAxis,
  yAxis,
}) => {
  return (
    <svg
      style={{ position: 'absolute', top: 0, left: 0 }}
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      stroke={illustrationColor.grayChateau}
    >
      {/* "minor" grid lines */}
      {logicalTickValues(
        xMin,
        xMax,
        xInterval / xAxis.intervalSubdivisions,
        xScale,
      ).map((v) => (
        <line
          key={v}
          x1={toPxX(v)}
          y1={0}
          x2={toPxX(v)}
          y2={height}
          stroke={illustrationColor.porcelain}
        />
      ))}
      {logicalTickValues(
        yMin,
        yMax,
        yInterval / yAxis.intervalSubdivisions,
        yScale,
      ).map((v) => (
        <line
          key={v}
          x1={0}
          y1={toPxY(v)}
          x2={width}
          y2={toPxY(v)}
          stroke={illustrationColor.porcelain}
        />
      ))}

      {/* "major" grid lines */}
      {logicalTickValues(xMin, xMax, xInterval, xScale).map((v) => (
        <line key={v} x1={toPxX(v)} y1={0} x2={toPxX(v)} y2={height} />
      ))}
      {logicalTickValues(yMin, yMax, yInterval, yScale).map((v) => (
        <line key={v} x1={0} y1={toPxY(v)} x2={width} y2={toPxY(v)} />
      ))}
    </svg>
  );
};

type Board = Object;
type BoardElement = Object;
type Callback = (board: Board) => BoardElement;
function useCreateBoardElement(cb: Callback) {
  // TODO memoize cb and then add memoizedCb and board as deps to useEffect
  const board = useBoard();

  // useEffect instead of useLayoutEffect because this is JSXGraph performance
  // is abysmal and we don't want to block the ui thread. JSXGraph typically
  // takes 120ms to render a single function plot 😱
  React.useEffect(() => {
    // TODO something about deleting and re-creating elements
    // is locking up the main thread with graphs with lots of elements.
    // Maybe JSXGraph is just not very performant. Either way we need
    // to dig into this as its annoying af in the editor.
    const element = cb(board);
    return () => {
      board.removeObject(element);
    };
  }, [board, cb]);
}

function useBoard() {
  const board = React.useContext(BoardContext);
  if (board === null) throw Error('board must be defined');
  return board;
}

type PointT = [number, number];
type PlotColor = $Keys<typeof plotColors>;

const pointSpec = t.tuple([t.number, t.number]);
const plotColorSpec = t.unionOfValues(...Object.keys(plotColors));

type PointProps = {|
  x: number,
  y: number,
  label?: string,
  labelLatex?: string,
  color?: PlotColor,
  open?: boolean,
|};
function Point(props: PointProps) {
  const {
    x,
    y,
    label,
    labelLatex,
    color = 'pickledBluewood',
    open = false,
  } = props;
  const [labelCoords, setLabelCoordsForElement] = useLabelCoords();

  const createBoardElement = React.useCallback(
    (board) => {
      const point = board.create('point', [x, y], {
        face: 'circle',
        strokeOpacity: open ? 1 : 0,
        // Using the PLANE_BG is a non-ideal way to achieve a "clipping mask"
        // when the point is meant to represent a non-inclusive end bound of
        // some plot (such as a line in an inequality). Without this "clipping mask"
        // you would see the underlying line go into the center of the open point.
        fillColor: open ? PLANE_BG : plotColors[color],
        size: 3,
        strokeColor: plotColors[color],
        withLabel: Boolean(label),
        name: label,
      });
      if (labelLatex) setLabelCoordsForElement(point);
      return point;
    },
    [x, y, label, labelLatex, color, open],
  );
  useCreateBoardElement(createBoardElement);

  return labelLatex !== undefined && labelCoords !== null ? (
    <LabelLatex label={labelLatex} coords={labelCoords} />
  ) : null;
}
Point.spec = {
  x: t.number,
  y: t.number,
  labelLatex: t.optional(t.latex),
  color: t.optional(plotColorSpec),
  open: t.optional(t.boolean),
};
Point = withValidation(Point);

// See http://jsxgraph.uni-bayreuth.de/docs/symbols/JXG.GeometryElement.html#dash
// 2 produces a 5px dash, 5px gap pattern. This is the closest to our style guide.
// One of the dash variant there is a 6px dash, 3px gap pattern.
const DASH_STYLE = 2;

type LineProps = {|
  p1: PointT,
  p2: PointT,
  color?: PlotColor,
  dashed?: boolean,
  // Notifies the owner when the board element for the function plot
  // changes. This allows the owner to compose the function plot when creating
  // its own board element (FunctionInequality being the canonical example of this).
  _onChangeBoardElement?: (boardElement: Object) => void,
  _ownerComponentName?: string,
  _color?: PlotColor,
  _dashed?: boolean,
|};
function Line(props: LineProps) {
  const {
    p1,
    p2,
    _onChangeBoardElement,
    _ownerComponentName,
    _dashed,
    _color,
  } = props;
  // If our owner has configured the color+dashed values use them instead.
  const color = _color ?? props.color ?? 'mountainMeadow';
  const dashed = _dashed ?? props.dashed ?? false;
  const stableP1 = useSamePointerForSameValue(p1);
  const stableP2 = useSamePointerForSameValue(p2);

  // We do some custom props validation so that dashed/color are not allowed when a Line
  // is used as a child of CartesianPlane
  const { failedValidation: failedContextualValidation } = useCustomValidation(
    props,
    function validatePropsContextually(addErrors, locations) {
      if (_ownerComponentName === 'FunctionInequality') {
        const errors = [
          props.dashed !== undefined && {
            message: `<Line> must NOT be given a \`dashed\` prop when used inside <FunctionInequality>`,
            ...locations.dashed,
          },
          props.color !== undefined && {
            message: `<Line> must NOT be given a \`color\` prop when used inside <FunctionInequality>`,
            ...locations.color,
          },
        ].filter(Boolean);
        if (errors.length > 0) addErrors(errors);
      }
    },
  );

  const board = useBoard();
  const isUnmounting = useIsUnmounting();
  const prevBoardElement = React.useRef(null);
  React.useEffect(() => {
    if (failedContextualValidation) return;
    board.removeObject(prevBoardElement.current);
    const boardElement = board.create('line', [stableP1, stableP2], {
      straightFirst: true,
      straightLast: true,
      ...strokeStyles,
      strokeColor: plotColors[color],
      ...(dashed ? { dash: DASH_STYLE } : {}),
    });
    prevBoardElement.current = boardElement;
    _onChangeBoardElement?.(boardElement);
    return () => {
      if (isUnmounting()) {
        board.removeObject(boardElement);
        _onChangeBoardElement?.(null);
      }
    };
  }, [
    color,
    dashed,
    failedContextualValidation,
    board,
    stableP1,
    stableP2,
    _onChangeBoardElement,
    isUnmounting,
  ]);

  if (failedContextualValidation) return null;
  return null;
}
Line.spec = {
  p1: pointSpec,
  p2: pointSpec,
  color: t.optional(plotColorSpec),
  dashed: t.optional(t.boolean),
};
Line = withValidation(Line);

type RayProps = {|
  p1: PointT,
  p2: PointT,
  color?: PlotColor,
  dashed?: boolean,
|};
function Ray(props: RayProps) {
  const { p1, p2, color = 'mountainMeadow', dashed = false } = props;
  const stableP1 = useSamePointerForSameValue(p1);
  const stableP2 = useSamePointerForSameValue(p2);
  const createBoardElement = React.useCallback(
    (board) => {
      return board.create('line', [stableP1, stableP2], {
        straightFirst: false,
        straightLast: true,
        ...strokeStyles,
        strokeColor: plotColors[color],
        ...(dashed ? { dash: DASH_STYLE } : {}),
      });
    },
    [stableP1, stableP2, color, dashed],
  );
  useCreateBoardElement(createBoardElement);
  return null;
}
Ray.spec = {
  p1: pointSpec,
  p2: pointSpec,
  color: t.optional(plotColorSpec),
  dashed: t.optional(t.boolean),
};
Ray = withValidation(Ray);

type LineSegmentProps = {|
  p1: PointT,
  p2: PointT,
  color?: PlotColor,
  dashed?: boolean,
|};
function LineSegment(props: LineSegmentProps) {
  const { p1, p2, color = 'mountainMeadow', dashed = false } = props;
  const stableP1 = useSamePointerForSameValue(p1);
  const stableP2 = useSamePointerForSameValue(p2);
  const createBoardElement = React.useCallback(
    (board) => {
      return board.create('line', [stableP1, stableP2], {
        straightFirst: false,
        straightLast: false,
        ...strokeStyles,
        strokeColor: plotColors[color],
        ...(dashed ? { dash: DASH_STYLE } : {}),
      });
    },
    [stableP1, stableP2, color, dashed],
  );
  useCreateBoardElement(createBoardElement);
  return null;
}
LineSegment.spec = {
  p1: pointSpec,
  p2: pointSpec,
  color: t.optional(plotColorSpec),
  dashed: t.optional(t.boolean),
};
LineSegment = withValidation(LineSegment);

type VectorProps = {|
  p1: PointT,
  p2: PointT,
  color?: PlotColor,
  dashed?: boolean,
  label?: string,
  labelLatex?: string,
|};
function Vector(props: VectorProps) {
  const {
    p1,
    p2,
    color = 'mountainMeadow',
    label,
    labelLatex,
    dashed = false,
  } = props;
  const stableP1 = useSamePointerForSameValue(p1);
  const stableP2 = useSamePointerForSameValue(p2);
  const [labelCoords, setLabelCoordsForElement] = useLabelCoords();

  const createBoardElement = React.useCallback(
    (board) => {
      const line = board.create('line', [stableP1, stableP2], {
        straightFirst: false,
        straightLast: false,
        lastArrow: true,
        ...strokeStyles,
        strokeColor: plotColors[color],
        withLabel: Boolean(label),
        label: {
          position: 'top',
          offset: [2, -5],
          anchorX: 'left',
          anchorY: 'top',
        },
        name: label,
        ...(dashed ? { dash: DASH_STYLE } : {}),
      });
      if (labelLatex) setLabelCoordsForElement(line);
      return line;
    },
    [stableP1, stableP2, color, label, labelLatex, dashed],
  );
  useCreateBoardElement(createBoardElement);

  return labelLatex !== undefined && labelCoords !== null ? (
    <LabelLatex label={labelLatex} coords={labelCoords} />
  ) : null;
}
Vector.spec = {
  p1: pointSpec,
  p2: pointSpec,
  color: t.optional(plotColorSpec),
  labelLatex: t.optional(t.latex),
  dashed: t.optional(t.boolean),
};
Vector = withValidation(Vector);

type FunctionPlotProps = {|
  // TODO remove formula as it has been replaced by `f`. Not removing
  // it for now as that would be a breaking change. I have removed it
  // from the spec though so its not valid to author with it anymore.
  formula: string,
  // The function to plot. They look like this: f={x => x + 3}
  f: Function,
  domain?: PointT,
  color?: PlotColor,
  dashed?: boolean,
  label?: string,
  labelLatex?: string,
  // Notifies the owner when the board element for the function plot
  // changes. This allows the owner to compose the function plot when creating
  // its own board element (FunctionInequality being the canonical example of this).
  _onChangeBoardElement?: (boardElement: Object) => void,
  _ownerComponentName?: string,
  _color?: PlotColor,
  _dashed?: boolean,
|};

function FunctionPlot(props: FunctionPlotProps) {
  const {
    // TODO remove formula. Not removing it for now as that
    // would be a breaking change. I have removed it from the spec
    // though so its not valid to author with it anymore.
    formula,
    f,
    domain = [],
    label,
    labelLatex,
    _onChangeBoardElement,
    _ownerComponentName,
    _dashed,
    _color,
  } = props;
  // If our owner has configured the color+dashed values use them instead.
  const color = _color ?? props.color ?? 'mountainMeadow';
  const dashed = _dashed ?? props.dashed ?? false;
  const stableF = useSamePointerForSameValue(f);
  const stableDomain = useSamePointerForSameValue(domain);
  const [labelCoords, setLabelCoordsForElement] = useLabelCoords();

  // We do some custom props validation so that dashed/color are not allowed when a FunctionPlot
  // is used as a child of CartesianPlane
  const { failedValidation: failedContextualValidation } = useCustomValidation(
    props,
    function validatePropsContextually(addErrors, locations) {
      if (_ownerComponentName === 'FunctionInequality') {
        const errors = [
          props.dashed !== undefined && {
            message: `<FunctionPlot> must NOT be given a \`dashed\` prop when used inside <FunctionInequality>`,
            ...locations.dashed,
          },
          props.color !== undefined && {
            message: `<FunctionPlot> must NOT be given a \`color\` prop when used inside <FunctionInequality>`,
            ...locations.color,
          },
        ].filter(Boolean);
        if (errors.length > 0) addErrors(errors);
      }
    },
  );

  const board = useBoard();
  const isUnmounting = useIsUnmounting();
  const prevBoardElement = React.useRef(null);
  React.useEffect(() => {
    if (failedContextualValidation) return;
    board.removeObject(prevBoardElement.current);
    const boardElement = board.create(
      'functiongraph',
      [stableF || formula, ...stableDomain],
      {
        straightFirst: false,
        straightLast: false,
        ...strokeStyles,
        strokeColor: plotColors[color],
        withLabel: Boolean(label) || Boolean(labelLatex),
        name: label,
        ...(dashed ? { dash: DASH_STYLE } : {}),
      },
    );
    if (labelLatex) setLabelCoordsForElement(boardElement);
    prevBoardElement.current = boardElement;
    _onChangeBoardElement?.(boardElement);
    return () => {
      if (isUnmounting()) {
        board.removeObject(boardElement);
        _onChangeBoardElement?.(null);
      }
    };
  }, [
    formula,
    stableF,
    stableDomain,
    color,
    label,
    labelLatex,
    dashed,
    failedContextualValidation,
    board,
    isUnmounting,
    setLabelCoordsForElement,
    _onChangeBoardElement,
  ]);

  if (failedContextualValidation) return null;
  if (labelLatex !== undefined && labelCoords !== null) {
    return <LabelLatex label={labelLatex} coords={labelCoords} />;
  }
  return null;
}
FunctionPlot.spec = {
  // NOTE more stringent restriction on the AST of f are enforced in validateAst
  f: t.optional(t.function),
  domain: t.optional(t.tuple([t.number, t.number])),
  // NOTE both `color` and `dashed` usage is restricted from being
  // used inside <FunctionInequality> using a custom validator.
  color: t.optional(plotColorSpec),
  dashed: t.optional(t.boolean),
  labelLatex: t.optional(t.latex),
};
FunctionPlot = withValidation(FunctionPlot);

type FunctionInequalityProps = {|
  direction: '>=' | '<=' | '>' | '<',
  color?: PlotColor,
  children: Element<typeof FunctionPlot | typeof Line>,
|};

function FunctionInequality({
  direction,
  color = 'mountainMeadow',
  children,
}: FunctionInequalityProps) {
  const board = useBoard();
  const childBoardElement = React.useRef(null);
  const prevBoardElement = React.useRef(null);
  const prevChildBoardElement = React.useRef(null);
  const isUnmounting = useIsUnmounting();
  const fillColor = plotColors[color];

  const createInequality = React.useCallback(
    (childBoardElement) => {
      return board.create('inequality', [childBoardElement], {
        inverse: shouldInverseInequality(direction, childBoardElement),
        fillColor,
        opacity: 0.3,
      });
    },
    [board, direction, fillColor],
  );
  const prevCreateInequality = usePrevious(createInequality);

  React.useEffect(() => {
    if (childBoardElement.current === null) {
      if (prevBoardElement.current !== null) {
        board.removeObject(prevBoardElement.current);
        prevBoardElement.current = null;
      }
      return;
    }

    let boardElement;
    if (
      prevChildBoardElement.current === childBoardElement.current &&
      prevBoardElement.current !== null &&
      prevCreateInequality === createInequality
    ) {
      boardElement = prevBoardElement.current;
    } else {
      board.removeObject(prevBoardElement.current);
      boardElement = createInequality(childBoardElement.current);
    }
    prevChildBoardElement.current = childBoardElement.current;
    prevBoardElement.current = boardElement;

    return () => {
      if (isUnmounting()) board.removeObject(boardElement);
    };
  });

  const _onChangeBoardElement = React.useCallback((element) => {
    childBoardElement.current = element;
  }, []);

  return React.Children.toArray(children).map((child) =>
    React.cloneElement(child, {
      _ownerComponentName: 'FunctionInequality',
      _dashed: ['>', '<'].includes(direction),
      // We always want the child element's color to correspond to the
      // fill color of the inequality.
      _color: color,
      _onChangeBoardElement,
    }),
  );
}
FunctionInequality.spec = {
  direction: t.unionOfValues('>=', '<=', '>', '<'),
  color: t.optional(plotColorSpec),
  children: t.unionOfTypes(t.elementType(FunctionPlot), t.elementType(Line)),
};
FunctionInequality = withValidation(FunctionInequality);

function shouldInverseInequality(direction, childBoardElement) {
  if (childBoardElement.getType() === 'line') {
    // Whether an inequality is drawn above/below is dependent on the vector
    // of the two points used to define the line. We don't want this behaviour
    // so we need to inverse the inequality when jsxgraph would shade the wrong
    // region.
    if (['<=', '<'].includes(direction)) {
      return inequalityIsShownAboveLine(childBoardElement);
    }
    if (['>=', '>'].includes(direction)) {
      return inequalityIsShownAboveLine(childBoardElement) === false;
    }
  }
  return ['>=', '>'].includes(direction);
}

function inequalityIsShownAboveLine(lineElement) {
  const angle = lineElement.getAngle();
  const π = Math.PI;
  return angle >= -π / 2 && angle < π / 2;
}

type PolygonProps = {| points: PointT[], color?: PlotColor, dashed?: boolean |};
function Polygon(props: PolygonProps) {
  const { points, color = 'mountainMeadow', dashed = false } = props;
  const stablePoints = useSamePointerForSameValue(points);
  const createBoardElement = React.useCallback(
    (board) => {
      return board.create('polygon', stablePoints, {
        withLines: true,
        hasInnerPoints: false,
        fillColor: '#CAEFE7',
        borders: {
          ...strokeStyles,
          strokeColor: plotColors[color],
          ...(dashed ? { dash: DASH_STYLE } : {}),
        },
        vertices: {
          visible: false,
        },
      });
    },
    [stablePoints, color, dashed],
  );
  useCreateBoardElement(createBoardElement);
  return null;
}
Polygon.spec = {
  points: t.arrayOf(pointSpec),
  color: t.optional(plotColorSpec),
  dashed: t.optional(t.boolean),
};
Polygon = withValidation(Polygon);

type CircleProps = {|
  center: PointT,
  radius: number,
  color?: PlotColor,
  dashed?: boolean,
|};
function Circle(props: CircleProps) {
  const { center, radius, color = 'mountainMeadow', dashed = false } = props;
  const stableCenter = useSamePointerForSameValue(center);
  const createBoardElement = React.useCallback(
    (board) => {
      return board.create('circle', [stableCenter, radius], {
        straightFirst: false,
        straightLast: false,
        ...strokeStyles,
        strokeColor: plotColors[color],
        ...(dashed ? { dash: DASH_STYLE } : {}),
      });
    },
    [stableCenter, radius, color, dashed],
  );
  useCreateBoardElement(createBoardElement);
  return null;
}
Circle.spec = {
  center: pointSpec,
  radius: t.number,
  color: t.optional(plotColorSpec),
  dashed: t.optional(t.boolean),
};
Circle = withValidation(Circle);

type EllipseProps = {|
  focus1: PointT,
  focus2: PointT,
  mainAxisLength: number,
  color?: PlotColor,
  dashed?: boolean,
|};
function Ellipse(props: EllipseProps) {
  const {
    focus1,
    focus2,
    mainAxisLength,
    color = 'mountainMeadow',
    dashed = false,
  } = props;
  const stableFocus1 = useSamePointerForSameValue(focus1);
  const stableFocus2 = useSamePointerForSameValue(focus2);
  const createBoardElement = React.useCallback(
    (board) => {
      return board.create(
        'ellipse',
        [stableFocus1, stableFocus2, mainAxisLength],
        {
          ...strokeStyles,
          strokeColor: plotColors[color],
          ...(dashed ? { dash: DASH_STYLE } : {}),
        },
      );
    },
    [stableFocus1, stableFocus2, mainAxisLength, color, dashed],
  );
  useCreateBoardElement(createBoardElement);
  return null;
}
Ellipse.spec = {
  focus1: pointSpec,
  focus2: pointSpec,
  mainAxisLength: t.number,
  color: t.optional(plotColorSpec),
  dashed: t.optional(t.boolean),
};
Ellipse = withValidation(Ellipse);

type HyperbolaProps = {|
  focus1: PointT,
  focus2: PointT,
  mainAxisLength: number,
  color?: PlotColor,
  dashed?: boolean,
|};
function Hyperbola(props: HyperbolaProps) {
  const {
    focus1,
    focus2,
    mainAxisLength,
    color = 'mountainMeadow',
    dashed = false,
  } = props;
  const stableFocus1 = useSamePointerForSameValue(focus1);
  const stableFocus2 = useSamePointerForSameValue(focus2);
  const createBoardElement = React.useCallback(
    (board) => {
      return board.create(
        'hyperbola',
        [stableFocus1, stableFocus2, mainAxisLength],
        {
          ...strokeStyles,
          strokeColor: plotColors[color],
          ...(dashed ? { dash: DASH_STYLE } : {}),
        },
      );
    },
    [stableFocus1, stableFocus2, mainAxisLength, color, dashed],
  );
  useCreateBoardElement(createBoardElement);
  return null;
}
Hyperbola.spec = {
  focus1: pointSpec,
  focus2: pointSpec,
  mainAxisLength: t.number,
  color: t.optional(plotColorSpec),
  dashed: t.optional(t.boolean),
};
Hyperbola = withValidation(Hyperbola);

type ParabolaProps = {|
  focus: PointT,
  directrix: [PointT, PointT],
  color?: PlotColor,
  // TODO need to figure out the correct semantics for dashed here
  // since Parabola has a directrix line as well as the parabola itself.
  // Should we dash both of these, just the directrix, just the parabola?
  // Or should we change the API entirely in some other way?
  dashed?: boolean,
|};
function Parabola(props: ParabolaProps) {
  const { focus, directrix, color = 'mountainMeadow', dashed = false } = props;
  const stableFocus = useSamePointerForSameValue(focus);
  const stableDirectrix = useSamePointerForSameValue(directrix);
  const createBoardElement = React.useCallback(
    (board) => {
      // TODO 🚨
      // There is a bug here. When the parabola element gets cleaned up
      // it doesn't cleanup its "child" directrixLine. Thanks JSXGraph!
      // This means that every time you change the props to <Parabola>
      // a new directrixLine will be added to the board and never cleaned up.
      // This gives visual bugs, and is also a memory leak.
      const directrixLine = board.create('line', [...stableDirectrix], {
        ...strokeStyles,
        strokeColor: plotColors[color],
        ...(dashed ? { dash: DASH_STYLE } : {}),
      });
      return board.create('parabola', [stableFocus, directrixLine], {
        ...strokeStyles,
        strokeColor: plotColors[color],
        ...(dashed ? { dash: DASH_STYLE } : {}),
      });
    },
    [stableFocus, stableDirectrix, color, dashed],
  );
  useCreateBoardElement(createBoardElement);
  return null;
}
Parabola.spec = {
  focus: pointSpec,
  directrix: t.tuple([pointSpec, pointSpec]),
  color: t.optional(plotColorSpec),
  dashed: t.optional(t.boolean),
};
Parabola = withValidation(Parabola);

// In a typical React setup, if objects/arrays you are passing as
// props are causing performance issues due to pointer values unecessarily
// changing (which cause React.memo shallow equality checks to fail),
// you can use standard programming techniques to ensure pointer
// equality for same-value data structures. For example:
//
// <Component value={[1, 2, 3]} />  // Perf issues
//
// let [value] = React.useState([1, 2, 3])
// <Component value={value} /> // Perf issues fixed
//
// But because we have content authors writing documents, we do not
// want any of this complexity to leak into the JSX document they
// are writing. Enter this hook. It is for use internally within
// the component (as opposed to at the call site of the component).
// It ensures that if pointer-equality is maintained for `value`
// so long as the new value received in a given render cycle is
// deeply-equal with the prior one.
function useSamePointerForSameValue(value) {
  const ref = React.useRef(value);
  const isSameValue =
    typeof value === 'function'
      ? String(value) === String(ref.current) // This compares implementation equality
      : isEqual(value, ref.current);
  React.useEffect(() => {
    if (isSameValue) return;
    ref.current = value;
  });
  return isSameValue ? ref.current : value;
}

function useLabelCoords() {
  const [labelCoords, setLabelCoords] = React.useState(null);

  const setLabelCoordsForElement = React.useCallback((element) => {
    // Render an invisible label so we can grab the layout
    element.setLabel('');
    const { top, left } = element.label.rendNode.style;
    setLabelCoords({ top, left });
  }, []);

  return [labelCoords, setLabelCoordsForElement];
}

function LabelLatex({ label, coords }) {
  return (
    <LatexInternal
      expression={label}
      style={{
        // This puts a 1px outline around the glyphs in order to increase readability
        textShadow: `
          -1px -1px 0 #fff,
          0 -1px 0 #fff,
          1px -1px 0 #fff,
          1px 0 0 #fff,
          1px 1px 0 #fff,
          0 1px 0 #fff,
          -1px 1px 0 #fff,
          -1px 0 0 #fff
        `,
        position: 'absolute',
        zIndex: 1,
        ...coords,
      }}
    />
  );
}

// Schedules a microtask. Microtasks are run at the end of the current task,
// before any other macro task can run (and before control is yieled to the
// browser)
function scheduleMicroTask(task) {
  Promise.resolve().then(task);
}

function useIsUnmounting() {
  const isUnmountingRef = React.useRef(false);
  React.useEffect(() => {
    return () => {
      isUnmountingRef.current = true;
    };
  }, []);
  const isUnmounting = React.useCallback(() => {
    return isUnmountingRef.current;
  }, []);
  return isUnmounting;
}

function usePrevious(value) {
  const ref = React.useRef(undefined);
  React.useEffect(() => {
    ref.current = value;
  });
  return ref.current;
}

export {
  CartesianPlane,
  Point,
  Line,
  Ray,
  LineSegment,
  Vector,
  FunctionPlot,
  FunctionInequality,
  Polygon,
  Circle,
  Ellipse,
  Hyperbola,
  Parabola,
};
